import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useI18n } from '@/lib/i18n';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const About = () => {
  const { lang, t } = useI18n();
  const sectionRef = useRef<HTMLDivElement>(null);

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const title = lang === 'ru' ? settings?.about_title_ru : settings?.about_title_en;
  const text = lang === 'ru' ? settings?.about_text_ru : settings?.about_text_en;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'start 0.3'],
  });

  // Scale from small (0.4) to full (1)
  const scale = useTransform(scrollYProgress, [0, 1], [0.4, 1]);
  // Round corners from large to smaller (keep rounded)
  const borderRadius = useTransform(scrollYProgress, [0, 1], [48, 16]);
  // Text appears after scale animation completes
  const textOpacity = useTransform(scrollYProgress, [0.85, 1], [0, 1]);
  const textY = useTransform(scrollYProgress, [0.85, 1], [30, 0]);
  return (
    <section id="about" ref={sectionRef} className="relative min-h-[200vh]">
      {/* Expanding photo */}
      <div className="sticky top-0 h-screen w-full overflow-hidden p-4 md:p-6">
        {/* Scaling photo only */}
        <motion.div
          style={{ scale, borderRadius }}
          className="absolute inset-4 md:inset-6 origin-center overflow-hidden rounded-2xl"
        >
          {settings?.about_image_url ? (
            <img
              src={settings.about_image_url}
              alt="About"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-secondary flex items-center justify-center">
              <span className="text-6xl font-display font-bold text-muted-foreground/20">✦</span>
            </div>
          )}
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40" />
        </motion.div>

        {/* Text outside scaling container */}
        <motion.div style={{ opacity: textOpacity, y: textY }} className="absolute inset-4 md:inset-6 flex items-end">
          <div className="container pb-16 md:pb-24">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 leading-tight text-[#f3f6f7]">
              {title || t('Обо мне', 'About me')}
            </h2>
            <div className="text-[#f3f6f7]/80 text-lg leading-relaxed max-w-2xl space-y-4">
              {text ? (
                text.split('\n').map((p, i) => <p key={i}>{p}</p>)
              ) : (
                <>
                  <p>{t('Креативный арт-директор.', 'Creative art director.')}</p>
                  <p>{t(
                    'Создаю визуальные концепции для шоу и рекламы — от идеи до целостного образа.',
                    'I create visual concepts for shows and advertising — from idea to cohesive image.'
                  )}</p>
                </>
              )}
            </div>
            <Link to="/about" className="inline-block mt-6 text-[#9aff03] text-sm uppercase tracking-widest hover:text-[#9aff03]/60 transition-colors">
              {t('Подробнее', 'Learn more')}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
